import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useOrderByParams } from '~/hooks/order-by';
import CreateEventTypeIllustration from '~/images/illustrations/create-event-type.svg';
import type { NetworkEventTypes__EventType as EventType } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateEventTypeIllustration}
      description={t('pages.event_types.empty_state.description')}
      header={t('pages.event_types.empty_state.header')}
    />
  );
};

export interface TableProps {
  eventTypes: readonly EventType[];
  loading: boolean;
}

export const Table = ({ eventTypes, loading }: TableProps) => {
  const columns = useColumns();
  const [eventTypeColumns, setColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isXLargeAndDown = useMediaQuery(theme.breakpoints.down('xl'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setColumns((x) => ({
      ...x,
      contentStartTime: !isSmallAndDown,
      contentStopTime: !isSmallAndDown,
      sponsorStartTime: !isXLargeAndDown,
      sponsorStopTime: !isXLargeAndDown,
    }));
  }, [isSmallAndDown, isXLargeAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={eventTypeColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setColumns}
      onRowClick={({ row: eventType }: GridRowParams<EventType>) => navigate(String(eventType.id))}
      rows={eventTypes}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
